import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_LEGACY_DIALOG_DATA } from '@angular/material/legacy-dialog';
import { MatLegacyTableModule } from '@angular/material/legacy-table';
import { ButtonComponent, BadgeComponent, IconComponent } from '@mode/capra';
import { MetricFacade } from '@mode/metrics/domain';
import { MetricsDomainModule } from '@mode/metrics/domain';
import { MetricDomain } from '@mode/shared/contract-common';
import { DialogHeaderComponent, DialogService } from '@mode/shared/ui';
import { AnalyticsEvent, AnalyticsService, RunMethodType } from '@mode/shared/util-segment';
import { Observable, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

@Component({
  standalone: true,
  imports: [BadgeComponent, ButtonComponent, CommonModule, DialogHeaderComponent, IconComponent, MatLegacyTableModule],
  selector: 'mode-metrics-dialog',
  templateUrl: './metrics-dialog.component.html',
  styleUrls: ['./metrics-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MetricsDialogComponent implements OnInit, OnDestroy {
  protected destroyed$ = new Subject<void>();

  isMetricsComplete$!: Observable<boolean>;
  metrics$!: Observable<readonly MetricDomain.Metric[]>;
  metricsError$!: Observable<Error | null>;

  selectedMetric?: MetricDomain.Metric;
  displayedColumns: string[] = ['metric', 'description', 'fields', 'connection'];
  dataSource: MetricDomain.Metric[] = [];

  selectedVisualization?: MetricDomain.VizTypeSelection;
  currentStep = 1;
  reportId: string;

  constructor(
    @Inject(MAT_LEGACY_DIALOG_DATA) data: { reportId?: string },
    private dialogService: DialogService,
    private metricFacade: MetricFacade,
    private analyticsService: AnalyticsService
  ) {
    this.reportId = data?.reportId ?? '';
  }

  ngOnInit() {
    this.metricFacade.metricDialogLoadMetrics();

    this.isMetricsComplete$ = this.metricFacade.isMetricsComplete$;
    this.metricsError$ = this.metricFacade.metricsError$;
    this.metrics$ = this.metricFacade.metrics$;
    this.sendModalAnalytics();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  onCancel() {
    this.dialogService.closeAll();
  }

  onRowClicked(metric: MetricDomain.Metric) {
    this.selectedMetric = metric;
  }

  onConfirm() {
    if (this.selectedMetric) {
      this.nextStep();

      this.analyticsService.run(RunMethodType.Track, AnalyticsEvent.Name.UiClicked, {
        location: AnalyticsEvent.SubContext.MetricBrowser,
        action: 'Use Metric',
      });
    }
  }

  nextStep(): void {
    this.currentStep++;
    this.sendModalAnalytics();
  }

  previousStep(): void {
    this.selectedVisualization = undefined;
    this.currentStep--;
    this.sendModalAnalytics();
  }

  sendModalAnalytics() {
    let modalType = AnalyticsEvent.SubContext.MetricBrowser;

    if (this.currentStep === 2) {
      modalType = AnalyticsEvent.SubContext.SelectMetricViz;
    }

    this.analyticsService.run(RunMethodType.Track, AnalyticsEvent.Name.ModalShown, {
      location: AnalyticsEvent.SubContext.ReportEmptyState,
      modal_type: modalType,
      context: AnalyticsEvent.Context.Report,
      context_id: this.reportId,
    });
  }

  onSelectVisualization(selection: MetricDomain.VizTypeSelection): void {
    this.selectedVisualization = selection;

    this.analyticsService.run(RunMethodType.Track, AnalyticsEvent.Name.UiClicked, {
      location: AnalyticsEvent.SubContext.SelectMetricViz,
      action: selection,
    });
  }

  gotoVisualization(): void {
    if (this.selectedMetric && this.selectedVisualization) {
      this.metricFacade.metricRouteSelectMetric(this.selectedMetric.token);
      this.metricFacade.metricRouteSelectVizType(this.selectedVisualization);

      // @todo: should be createMetricQuery?
      this.metricFacade.createMetric(this.selectedMetric.token, this.selectedVisualization);

      // navigate to loading screen
      this.currentStep++;

      // close when report run is complete
      this.metricFacade.isMetricRunComplete$
        .pipe(
          filter((isComplete) => isComplete),
          takeUntil(this.destroyed$)
        )
        .subscribe(() => {
          this.dialogService.closeAll();
        });

      this.analyticsService.run(RunMethodType.Track, AnalyticsEvent.Name.UiClicked, {
        location: AnalyticsEvent.SubContext.SelectMetricViz,
        action: 'visualize metric',
      });
    }
  }
}
